import {
    Component,
    Input,
    OnInit,
    ViewChild,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    ViewEncapsulation,
} from "@angular/core";
import { BarrierListFilter, sortList, LabelMap } from "@app/models/filters/barrierListFilter";
import { PaginateFilter } from "@app/models/filters/paginateFilter";
import { Barrier, BarrierList } from "@app/models/barrier";
import { PaginateList, Pagination } from "@app/models/paginateList";
import { BarrierService } from "@app/services/barrier.service";
import { AngularOpenlayersModule, LayerVectorComponent, StyleComponent } from "ng-openlayers";
import { Feature, MapBrowserEvent } from "ol";
import { createEmpty, extend } from "ol/extent";
import { Point } from "ol/geom";
import { Fill, Icon, Text, Circle, Style } from "ol/style";
import { Coordinate } from "ol/coordinate";
import { toLonLat } from "ol/proj";
import {
    ElementPlacement,
    ItSidebarComponent,
    ItAccordionComponent,
    ItRadioButtonComponent,
    ItCheckboxComponent,
    ItModalComponent,
    ItInputComponent,
    ItSelectComponent,
    SelectControlOption,
    AutocompleteItem,
    ItButtonDirective,
    ItBadgeDirective,
} from "design-angular-kit";
import { BarrierListComponent } from "../barrier-list/barrier-list.component";
import { TranslateModule } from "@ngx-translate/core";
import {
    baseOrto,
    catastoAE,
    InfiniteScrollElem,
    layersRasPath,
    limitiAmm,
    ortofotoRasPath,
    territorio,
} from "@app/app.constants";
import { FormGroup, FormsModule, ReactiveFormsModule, UntypedFormControl } from "@angular/forms";
import { AsyncPipe, KeyValuePipe } from "@angular/common";
import { MunicipalityNamePipe } from "@app/pipes/municipality.pipe";
import { PurposeNamePipe } from "@app/pipes/purpose.pipe";
import { DecoService } from "@app/services/deco.service";
import { DecoStoreService } from "@app/stores/deco.store";
import { ProvinceNamePipe } from "@app/pipes/province.pipe";
import { ItAutocompleteComponent } from "@app/components/custom/autocomplete/autocomplete.component";

@Component({
    selector: "app-barrier-map",
    standalone: true,
    imports: [
        AngularOpenlayersModule,
        BarrierListComponent,
        TranslateModule,
        ItSidebarComponent,
        ItAccordionComponent,
        ItRadioButtonComponent,
        ItModalComponent,
        ItCheckboxComponent,
        ItInputComponent,
        ItSelectComponent,
        ItAutocompleteComponent,
        FormsModule,
        ReactiveFormsModule,
        KeyValuePipe,
        MunicipalityNamePipe,
        PurposeNamePipe,
        AsyncPipe,
        ItButtonDirective,
        ProvinceNamePipe,
        ItBadgeDirective,
    ],
    templateUrl: "./barrier-map.component.html",
    styleUrls: ["../../../../styles.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class BarrierMapComponent implements OnInit, AfterViewInit {
    @Input() isFilterable: boolean = false;
    @Input() title!: string;
    @Input() showTitle: boolean = false;
    @Input() isPublic: boolean = false;
    @ViewChild("layer") layer!: LayerVectorComponent;
    @ViewChild("icon") icon!: StyleComponent;
    @ViewChild("cluster") cluster!: StyleComponent;
    @ViewChild("municipalityAutocomplete") municipalityAutocomplete!: ItAutocompleteComponent;
    @ViewChild("filterModal") filterModal!: ItModalComponent;
    items: BarrierList = new BarrierList();
    sortList = sortList;
    pagination: Pagination | null = null;
    searchFilters: PaginateFilter<BarrierListFilter> = new PaginateFilter();
    distance = 35;
    features: any[] = [];
    mapCenter: Coordinate = [9.0, 40.1];
    popupContent: { show: boolean; coord: Coordinate; contents?: Barrier[] | null } = {
        show: false,
        coord: this.mapCenter,
        contents: null,
    };
    placement: ElementPlacement = "top";
    collapsedSidebar = true;
    selectedBase = "osm";
    ortofotoRasPath = ortofotoRasPath;
    layersRasPath = layersRasPath;
    baseOrto = baseOrto;
    limitiAmm = limitiAmm;
    catastoAE = catastoAE;
    territorio = territorio;
    filters = new BarrierListFilter();
    filtersForm = new FormGroup({
        denominazione: new UntypedFormControl(this.filters.denominazione),
        indirizzo: new UntypedFormControl(this.filters.indirizzo),
        utilizzo: new UntypedFormControl(this.filters.utilizzo),
        comune: new UntypedFormControl(this.filters.comune),
        provincia: new UntypedFormControl(this.filters.provincia),
        codiceSbarramento: new UntypedFormControl(this.filters.codiceSbarramento),
        tutti: new UntypedFormControl(this.filters.tutti),
    });
    activeFilters: any;
    purposesDeco: SelectControlOption<number>[] = [];
    provicesDeco: SelectControlOption<number>[] = [];

    constructor(
        private barrierService: BarrierService,
        readonly decoService: DecoService,
        readonly decoStoreService: DecoStoreService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    clusterMemberStyle(clusterMember: Feature) {
        return new Style({
            geometry: clusterMember?.getGeometry(),
            image: new Icon({ src: "/assets/img/marker-blue.png" }),
        });
    }

    clusterStyle(feature: Feature[]) {
        const size = feature.length;
        if (size > 1) {
            return [
                new Style({ image: new Circle({ radius: 20, fill: new Fill({ color: "rgba(8, 99, 138, 0.3)" }) }) }),
                new Style({
                    image: new Circle({ radius: 14, fill: new Fill({ color: "rgba(8, 99, 138, 0.7)" }) }),
                    text: new Text({
                        text: size.toString(),
                        fill: new Fill({ color: "#fff" }),
                    }),
                }),
            ];
        } else {
            const originalFeature = feature[0];
            return this.clusterMemberStyle(originalFeature);
        }
    }

    ngOnInit() {
        if (this.isFilterable) {
            if (this.isPublic) {
                this.decoStoreService.getPurposesPublic().subscribe({ next: (res) => (this.purposesDeco = res) });
                this.decoStoreService.getProvinceNamesPublic().subscribe({ next: (res) => (this.provicesDeco = res) });
            } else {
                this.decoStoreService.getPurposes().subscribe({ next: (res) => (this.purposesDeco = res) });
                this.decoStoreService.getProvinceNames().subscribe({ next: (res) => (this.provicesDeco = res) });
            }
        }
        this.searchFilters.ordinamento = sortList[0];
        this.barrierService.barrierList.subscribe((barrierList: PaginateList<Barrier>) => {
            if (barrierList.ordinamento.campo) this.searchFilters.ordinamento = barrierList.ordinamento;
            this.changeDetectorRef.markForCheck();
        });
        this.barrierService.barrierListFilter.subscribe((barrierListFilter: BarrierListFilter) => {
            this.searchFilters.paginazione = { numeroPagina: 0, elementiPagina: InfiniteScrollElem };
            this.getBarriers();
        });
    }

    ngAfterViewInit() {
        this.initializeMapStyle();
    }

    initializeMapStyle() {
        if (this.layer && this.layer.instance) {
            this.layer.instance.setStyle((feature: Feature) => {
                return this.clusterStyle(feature.get("features"));
            });
        }
    }

    getBarriers() {
        this.popupContent.show = false;
        this.activeFilters = Object.fromEntries(
            Object.entries(this.filtersForm.value).filter(([_, v]) => v != "" && v != null && v != "null"),
        );
        this.searchFilters.filtri = this.filtersForm.getRawValue();

        const barrierServiceCall = this.isPublic
            ? this.barrierService.getAllPublic(this.searchFilters)
            : this.barrierService.getAll(this.searchFilters);

        barrierServiceCall.subscribe({
            next: (res) => {
                this.items.risultati = res.risultati.map((item) => new Barrier(item));
                this.pagination = res.paginazione;
                this.barrierService.setBarrierList(res);
                this.changeDetectorRef.markForCheck();
            },
        });
    }

    onSingleClick(event: MapBrowserEvent<MouseEvent>) {
        this.popupContent.show = false;
        const view = event.map!.getView();
        event.map.forEachFeatureAtPixel(event.pixel, (featuresGroup) => {
            const features = featuresGroup.get("features") as Feature<Point>[];
            const center = features[0].getGeometry()?.getCoordinates();
            if (center) this.popupContent.coord = toLonLat(center);

            if (features.length > 1) {
                const extent = createEmpty();
                features.forEach((feature) => {
                    const geometry = feature.getGeometry();
                    geometry && geometry.getExtent() && extend(extent, geometry.getExtent());
                });
                if (view.getZoom() === view.getMaxZoom()) {
                    view.animate({ center, duration: 500 });
                    this.setPopupContent(features);
                } else view.fit(extent, { duration: 500, padding: [100, 100, 100, 100] });
            } else {
                view.animate({ center, duration: 500 });
                this.setPopupContent(features);
            }
        });
    }

    setPopupContent(features: Feature<Point>[]) {
        this.popupContent.contents = features
            .map((elem) => this.items.risultati.find((x) => x.uid === elem.getId()))
            .filter((item): item is Barrier => item !== undefined);
        this.popupContent.show = true;
        this.changeDetectorRef.markForCheck();
    }

    toggleSidebar() {
        this.collapsedSidebar = !this.collapsedSidebar;
        this.changeDetectorRef.markForCheck();
    }

    isVisibleBase(code: string) {
        return this.selectedBase === code;
    }

    getFilterLabel(key: any) {
        return `it.portal.barriers.${LabelMap[key as keyof typeof LabelMap]}`;
    }

    cleanAllAutocomplete() {
        this.municipalityAutocomplete.writeValue("");
    }

    clearActiveFilters(filter: any) {
        filter !== "all" && this.filtersForm.get(filter)?.reset();
        switch (filter) {
            case "comune":
                this.municipalityAutocomplete.writeValue("");
                break;
            case "all":
                this.filtersForm.reset();
                this.cleanAllAutocomplete();
                break;
        }
        this.getBarriers();
    }

    onMunicipalitySelected(item: AutocompleteItem): void {
        this.filtersForm.patchValue({ comune: item.additionalData });
    }

    get resultsTranslationKey() {
        return `it.portal.results.${this.items.risultati?.length !== 1 ? "other" : "one"}`;
    }

    get isActiveFiltersEmpty() {
        return this.activeFilters ? Object.keys(this.activeFilters).length === 0 : true;
    }
}
